import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setEmail, removeEmail, resendConfirmation } from '../api/settings';

/**
 * Hook to manage email confirmation operations
 * Handles setting, removing, and resending confirmation emails
 */
export function useEmailConfirmation() {
  const queryClient = useQueryClient();

  // Set email mutation
  const setEmailMutation = useMutation<
    { message: string; email: string; email_confirmed: boolean },
    Error,
    string
  >({
    mutationFn: setEmail,
    onSuccess: () => {
      // Invalidate settings to refetch and get updated email status
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  // Remove email mutation
  const removeEmailMutation = useMutation<{ message: string }, Error, void>({
    mutationFn: removeEmail,
    onSuccess: () => {
      // Invalidate settings to refetch
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  // Resend confirmation mutation
  const resendConfirmationMutation = useMutation<{ message: string }, Error, void>({
    mutationFn: resendConfirmation,
  });

  return {
    // Set email
    setEmail: setEmailMutation.mutate,
    isSettingEmail: setEmailMutation.isPending,
    setEmailError: setEmailMutation.error,
    setEmailSuccess: setEmailMutation.isSuccess,
    setEmailData: setEmailMutation.data,

    // Remove email
    removeEmail: removeEmailMutation.mutate,
    isRemovingEmail: removeEmailMutation.isPending,
    removeEmailError: removeEmailMutation.error,
    removeEmailSuccess: removeEmailMutation.isSuccess,

    // Resend confirmation
    resendConfirmation: resendConfirmationMutation.mutate,
    isResending: resendConfirmationMutation.isPending,
    resendError: resendConfirmationMutation.error,
    resendSuccess: resendConfirmationMutation.isSuccess,
  };
}
